import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { driversService } from '../../services/driversService';
import { ordersService } from '../../services/ordersService';
import { AdminDriverDetailsResponse, ApplicationStatus, DriverState, AppState } from '../../types/drivers';
import { AdminType } from '../../types/auth';
import ChangeDriverStatusModal from '../modals/ChangeDriverStatusModal';
import DriverTransactions from '../driver/DriverTransactions';
import SendOrderToDriverModal from '../modals/SendOrderToDriverModal';
import ImageViewerModal from '../modals/ImageViewerModal';
import DriverStatisticsModal from '../modals/DriverStatisticsModal';
import LocationMap from '../map/LocationMap';
import { imageIdToUrl } from '../../utils/imageUtils';
import { useAuth } from '../../contexts/AuthContext';
import './Page.css';

const DriverDetails: React.FC = () => {
  const { driverId } = useParams<{ driverId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentAdmin } = useAuth();
  const isSuperAdmin = currentAdmin?.adminType === AdminType.SUPER_ADMIN;

  const [details, setDetails] = useState<AdminDriverDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal states
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isSendOrderModalOpen, setIsSendOrderModalOpen] = useState(false);
  const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Image viewer state
  const [viewerImage, setViewerImage] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    if (driverId) {
      fetchDriverDetails(parseInt(driverId));
    }
  }, [driverId]);

  const fetchDriverDetails = async (id: number) => {
    setLoading(true);
    setError('');

    try {
      const response = await driversService.getDriverDetails(id);

      if (response.success && response.data) {
        setDetails(response.data);
      } else {
        setError(response.error || t('driverDetails.failedToFetch'));
      }
    } catch (error) {
      setError(t('driverDetails.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (newStatus: ApplicationStatus, blockReason: string | null) => {
    if (!driverId) return;

    setModalLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await driversService.changeApplicationStatus({
        driverId: parseInt(driverId),
        applicationStatus: newStatus,
        blockReason: blockReason
      });

      if (response.success) {
        setSuccessMessage(t('driverDetails.statusUpdatedSuccess'));
        setIsStatusModalOpen(false);
        // Refresh the details
        await fetchDriverDetails(parseInt(driverId));
      } else {
        setError(response.error || t('driverDetails.failedToChangeStatus'));
      }
    } catch (error) {
      setError(t('driverDetails.networkError'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleSendOrderToDriver = async (orderId: number) => {
    if (!driverId) return;

    setModalLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await ordersService.sendOrderToDriver({
        orderId,
        driverId: parseInt(driverId)
      });

      if (response.success) {
        setSuccessMessage(t('driverDetails.orderSentSuccess'));
        setIsSendOrderModalOpen(false);
      } else {
        setError(response.error || t('driverDetails.failedToSendOrder'));
      }
    } catch (error) {
      setError(t('driverDetails.networkError'));
    } finally {
      setModalLoading(false);
    }
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString()} soum`;
  };

  const getStatusColor = (status: ApplicationStatus): string => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return '#ffc107';
      case ApplicationStatus.ACCEPTED:
        return '#28a745';
      case ApplicationStatus.BLOCKED:
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusDisplay = (status: ApplicationStatus): string => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return t('driverDetails.pending');
      case ApplicationStatus.ACCEPTED:
        return t('driverDetails.accepted');
      case ApplicationStatus.BLOCKED:
        return t('driverDetails.blocked');
      default:
        return status;
    }
  };

  const getDriverStateDisplay = (state: DriverState): string => {
    return state === DriverState.ACTIVE ? t('driverDetails.active') : t('driverDetails.passive');
  };

  const getDriverStateColor = (state: DriverState): string => {
    return state === DriverState.ACTIVE ? '#28a745' : '#6c757d';
  };

  const getAppStateDisplay = (appState: AppState): string => {
    return appState === AppState.FOREGROUND ? `🟢 ${t('driverDetails.online')}` : `🔴 ${t('driverDetails.offline')}`;
  };

  const getIsWorkingDisplay = (isWorking: boolean): string => {
    return isWorking ? t('drivers.working') : t('drivers.notWorking');
  };

  const getIsWorkingColor = (isWorking: boolean): string => {
    return isWorking ? '#28a745' : '#6c757d';
  };

  const formatDate = (timestamp: number, isMilliseconds: boolean = false): string => {
    const date = new Date(isMilliseconds ? timestamp : timestamp * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="loading-container">{t('driverDetails.loadingDriverDetails')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/drivers')} className="back-button">
            {t('driverDetails.backToDrivers')}
          </button>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="no-data">{t('driverDetails.driverNotFound')}</div>
          <button onClick={() => navigate('/drivers')} className="back-button">
            {t('driverDetails.backToDrivers')}
          </button>
        </div>
      </div>
    );
  }

  const { driver, driverState, lastLocation } = details;

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-with-back">
          <button onClick={() => navigate('/drivers')} className="back-button">
            ← {t('driverDetails.backToDrivers')}
          </button>
          <div>
            <h1>{t('driverDetails.title', { driverName: driver.fullName })}</h1>
            <p>{t('driverDetails.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* Success Message */}
        {successMessage && (
          <div className="success-message" style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {successMessage}
          </div>
        )}

        {/* Driver Info */}
        <div className="order-details-grid">
          <div className="detail-card">
            <h3>{t('driverDetails.basicInformation')}</h3>
            <div className="info-grid">
              <div>
                <strong>{t('driverDetails.id')}:</strong> {driver.id}
              </div>
              <div>
                <strong>{t('driverDetails.fullName')}:</strong> {driver.fullName}
              </div>
              <div>
                <strong>{t('driverDetails.phone')}:</strong> {driver.phoneNumber}
              </div>
              <div>
                <strong>{t('driverDetails.appState')}:</strong> {getAppStateDisplay(driver.appState)}
              </div>
              <div>
                <strong>{t('drivers.isWorking')}:</strong>{' '}
                <span
                  className="status-badge"
                  style={{ backgroundColor: getIsWorkingColor(driver.isWorking) }}
                >
                  {getIsWorkingDisplay(driver.isWorking)}
                </span>
              </div>
              <div>
                <strong>{t('driverDetails.driverState')}:</strong>{' '}
                <span
                  className="status-badge"
                  style={{ backgroundColor: getDriverStateColor(driverState) }}
                >
                  {getDriverStateDisplay(driverState)}
                </span>
              </div>
              <div>
                <strong>{t('driverDetails.applicationStatus')}:</strong>{' '}
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(driver.applicationStatus) }}
                >
                  {getStatusDisplay(driver.applicationStatus)}
                </span>
              </div>
            </div>
          </div>

          <div className="detail-card">
            <h3>{t('driverDetails.financial')}</h3>
            <div className="info-grid">
              <div>
                <strong>{t('driverDetails.balance')}:</strong> {formatPrice(driver.balance)}
              </div>
            </div>
          </div>
        </div>

        {/* Driver Transactions */}
        {isSuperAdmin && (
          <DriverTransactions
            driverId={driver.id}
            driverName={driver.fullName}
            onTransactionCreated={() => fetchDriverDetails(Number(driverId))}
          />
        )}

        {/* Block Reason if blocked */}
        {driver.applicationStatus === ApplicationStatus.BLOCKED && driver.applicationStatusBlockReason && (
          <div className="detail-card full-width" style={{ borderLeft: '4px solid #dc3545' }}>
            <h3>{t('driverDetails.blockReason')}</h3>
            <p style={{ color: '#dc3545' }}>{driver.applicationStatusBlockReason}</p>
          </div>
        )}

        {/* Admin Actions */}
        <div className="action-buttons">
          <h3>{t('driverDetails.adminActions')}</h3>
          <div className="action-buttons-grid">
            <button
              className="action-button accept"
              onClick={() => setIsStatusModalOpen(true)}
            >
              {t('driverDetails.changeStatus')}
            </button>
            <button
              className="action-button primary"
              onClick={() => setIsSendOrderModalOpen(true)}
            >
              {t('driverDetails.sendOrder')}
            </button>
            <button
              className="action-button accept"
              onClick={() => setIsStatisticsModalOpen(true)}
              style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
            >
              {t('statistics.driverStatistics')}
            </button>
          </div>
        </div>

        {/* Driver Images */}
        <div className="detail-card full-width">
          <h3>{t('driverDetails.driverDocuments')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div>
              <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>{t('driverDetails.profileImage')}</h4>
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onClick={() => setViewerImage({
                  url: imageIdToUrl(driver.imageId) || '',
                  title: `${driver.fullName} - Profile Image`
                })}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <img
                  src={imageIdToUrl(driver.imageId) || ''}
                  alt="Driver Profile"
                  style={{
                    width: '100%',
                    height: '250px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement!.innerHTML = `<div style="padding: 1rem; text-align: center; color: #666;">${t('driverDetails.imageNotAvailable')}</div>`;
                  }}
                />
              </div>
            </div>
            <div>
              <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>{t('driverDetails.vehicleImage')}</h4>
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onClick={() => setViewerImage({
                  url: imageIdToUrl(driver.vehicleImageId) || '',
                  title: `${driver.fullName} - Vehicle Image`
                })}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <img
                  src={imageIdToUrl(driver.vehicleImageId) || ''}
                  alt="Vehicle"
                  style={{
                    width: '100%',
                    height: '250px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement!.innerHTML = `<div style="padding: 1rem; text-align: center; color: #666;">${t('driverDetails.imageNotAvailable')}</div>`;
                  }}
                />
              </div>
            </div>
            <div>
              <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>{t('driverDetails.driverLicenseImage')}</h4>
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onClick={() => setViewerImage({
                  url: imageIdToUrl(driver.driverLicenceImageId) || '',
                  title: `${driver.fullName} - Driver License`
                })}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <img
                  src={imageIdToUrl(driver.driverLicenceImageId) || ''}
                  alt="Driver License"
                  style={{
                    width: '100%',
                    height: '250px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement!.innerHTML = `<div style="padding: 1rem; text-align: center; color: #666;">${t('driverDetails.imageNotAvailable')}</div>`;
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Last Location */}
        <div className="detail-card full-width">
          <h3>{t('driverDetails.lastKnownLocation')}</h3>
          {lastLocation ? (
            <>
              <div className="info-grid" style={{ marginBottom: '1rem' }}>
                <div>
                  <strong>{t('driverDetails.locationId')}:</strong> {lastLocation.id}
                </div>
                <div>
                  <strong>{t('driverDetails.coordinates')}:</strong> {lastLocation.latitude.toFixed(6)}, {lastLocation.longitude.toFixed(6)}
                </div>
                <div>
                  <strong>{t('driverDetails.heading')}:</strong> {lastLocation.heading.toFixed(2)}°
                </div>
                <div>
                  <strong>{t('driverDetails.appState')}:</strong> {getAppStateDisplay(lastLocation.appState)}
                </div>
                <div>
                  <strong>{t('driverDetails.lastUpdated')}:</strong> {formatDate(lastLocation.serverTimeIntervalUTC, true)}
                </div>
              </div>
              <LocationMap
                latitude={lastLocation.latitude}
                longitude={lastLocation.longitude}
                title={`${driver.fullName} - ${t('driverDetails.lastKnownLocation')}`}
                height="400px"
              />
            </>
          ) : (
            <div className="no-data">{t('driverDetails.noLocationData')}</div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ChangeDriverStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onSubmit={handleChangeStatus}
        currentStatus={driver.applicationStatus}
        loading={modalLoading}
      />

      <SendOrderToDriverModal
        isOpen={isSendOrderModalOpen}
        onClose={() => setIsSendOrderModalOpen(false)}
        onSend={handleSendOrderToDriver}
        driverName={driver.fullName}
        loading={modalLoading}
      />

      <DriverStatisticsModal
        isOpen={isStatisticsModalOpen}
        onClose={() => setIsStatisticsModalOpen(false)}
        driverId={driver.id}
        driverName={driver.fullName}
      />

      <ImageViewerModal
        isOpen={!!viewerImage}
        onClose={() => setViewerImage(null)}
        imageUrl={viewerImage?.url || null}
        imageTitle={viewerImage?.title}
      />
    </div>
  );
};

export default DriverDetails;
